import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

export type CancelReason = 'user_cancelled' | 'new_prompt' | 'timeout';

export type OutputType =
  | 'text'
  | 'search_results'
  | 'browse_result'
  | 'image'
  | 'table'
  | 'spawn_summary';

export type RuntimeMode = 'reused' | 'fresh_new' | 'fresh_after_loss' | 'initializing';

export type BridgeEvent =
  | UserMessageEvent
  | TextDeltaEvent
  | CodeStartEvent
  | CodeOutputEvent
  | CodeEndEvent
  | DoneEvent
  | CancelledEvent
  | AgentSpawnEvent
  | AgentCompleteEvent
  | AgentErrorEvent
  | RuntimeStateEvent
  | SubagentTextDeltaEvent
  | UserDownloadEvent
  | AuthRequestEvent
  | AuthResolvedEvent
  | WorkingStatusEvent;

export interface BaseEvent {
  seq: number;
  run_id: string;
  type: string;
  session_id: string;
}

export type RunSource = 'user' | 'self';

export interface UserMessageEvent extends BaseEvent {
  type: 'user_message';
  content: string;
  file_paths: string[];
  source?: RunSource;
}

export interface TextDeltaEvent extends BaseEvent {
  type: 'text_delta';
  content: string;
}

export interface CodeStartEvent extends BaseEvent {
  type: 'code_start';
  id: string;
  code: string;
}

export interface CodeOutputEvent extends BaseEvent {
  type: 'code_output';
  id: string;
  code?: string;
  output: string;
  output_type: OutputType;
  metadata: Record<string, unknown>;
}

export interface CodeEndEvent extends BaseEvent {
  type: 'code_end';
  id: string;
  status: 'success' | 'error';
  duration_ms: number | null;
}

export interface DoneEvent extends BaseEvent {
  type: 'done';
}

export interface CancelledEvent extends BaseEvent {
  type: 'cancelled';
  reason: CancelReason;
}

export interface AgentSpawnEvent extends BaseEvent {
  type: 'agent_spawn';
  id: number;
  parent_id: number | null;
  root_id: number;
  premise: string | null;
  task: string;
  started_at: string | null;
}

export interface AgentCompleteEvent extends BaseEvent {
  type: 'agent_complete';
  id: number;
  result_summary: string;
  duration_ms: number | null;
  completed_at: string | null;
}

export interface AgentErrorEvent extends BaseEvent {
  type: 'agent_error';
  id?: number;
  error: string;
}

export interface RuntimeStateEvent extends BaseEvent {
  type: 'runtime_state';
  runtime_id: string;
  mode: RuntimeMode;
  started_at: string;
}

export interface SubagentTextDeltaEvent extends BaseEvent {
  type: 'subagent_text_delta';
  agent_id: number;
  content: string;
  role: string;
}

export interface UserDownloadEvent extends BaseEvent {
  type: 'user_download';
  download_id: string;
  filename: string;
  size: number;
  label?: string;
}

export interface AuthRequestEvent extends BaseEvent {
  type: 'auth_request';
  provider: string;
  display_name: string;
  icon_url: string;
  auth_start_url: string;
}

export interface AuthResolvedEvent extends BaseEvent {
  type: 'auth_resolved';
  provider: string;
}

/**
 * Short user-facing phrase emitted by the agent's `report_partial_status`
 * REPL tool. The client simply reflects the latest value in the
 * `WorkingBubble`; an empty string means "fall back to the default
 * Working… label".
 */
export interface WorkingStatusEvent extends BaseEvent {
  type: 'working_status';
  status: string;
  agent_id?: number | null;
}

export interface UsageCostDetails {
  upstream_inference_cost: number;
  upstream_inference_prompt_cost: number;
  upstream_inference_completions_cost: number;
}

export interface UsageInfo {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cached_tokens: number;
  cache_write_tokens: number;
  inference_count: number;
  cost: number;
  cost_details: UsageCostDetails;
  context_window_tokens: number;
  context_cached_tokens: number;
  context_reasoning_tokens: number;
}

export type AgentStatus = 'starting' | 'on' | 'off' | 'stopping';

export interface Presentation {
  [key: string]: unknown;
}

export const parsePresentation = (raw: string): Presentation | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Presentation;
  } catch {
    return null;
  }
};

type HydratedFields = 'presentation' | 'last_interaction_at' | 'pinned';

type RawFields<T> = Omit<T, HydratedFields> & {
  presentation?: string | null;
  last_interaction_at: string | null;
};

export type RawSessionSummary = RawFields<SessionSummary>;
export type RawSessionDetail = RawFields<SessionDetail>;

export const hydrateSession = <T extends { presentation?: string | null; last_interaction_at: string | null }>(
  raw: T,
): Omit<T, 'presentation' | 'last_interaction_at'> & { presentation: Presentation | null; last_interaction_at: Dayjs | null; pinned: boolean } => {
  const presentation = parsePresentation(raw.presentation ?? "{}");
  return {
    ...raw,
    presentation,
    last_interaction_at: raw.last_interaction_at ? dayjs(raw.last_interaction_at) : null,
    pinned: presentation?.pinned === true,
  };
};

export interface SessionSummary {
  id: string;
  title: string;
  /** Optional inline SVG generated by the title agent. Renders as the
   *  session's pill glyph in the rail; falls back to the first letter of
   *  the title when missing. */
  title_icon_svg?: string | null;
  model: string | null;
  premise: string | null;
  presentation: Presentation | null;
  created_at: string;
  last_interaction_at: Dayjs | null;
  interaction_count: number;
  runtime_status: AgentStatus;
  active_run_id: string | null;
  runtime_id: string | null;
  runtime_started_at: string | null;
  usage: UsageInfo | null;
  pinned: boolean;
  is_example?: boolean;
  thumbnail_url?: string | null;
}

export interface SessionDetail {
  id: string;
  title: string;
  model: string | null;
  premise: string | null;
  presentation: Presentation | null;
  created_at: string;
  last_interaction_at: Dayjs | null;
  interaction_count: number;
  runtime_status: AgentStatus;
  active_run_id: string | null;
  runtime_id: string | null;
  pinned: boolean;
  runtime_started_at: string | null;
  usage: UsageInfo | null;
}

export type SessionEvent =
  | { type: 'session_updated'; session: SessionSummary }
  | { type: 'session_deleted'; session_id: string };

export type DemoPreviewHoverGradientTheme = 'dark' | 'light' | 'auto';

export interface DemoManifestEntry {
  id: string;
  name: string;
  title: string;
  premise: string | null;
  description: string | null;
  tags: string[] | null;
  model: string | null;
  version: number;
  thumbnail_url: string | null;
  mobile_order: number | null;
  // Forces the description hover overlay (gradient + text) to render against
  // a fixed surface tone regardless of the active app theme.
  //   'dark'  — dark surface + light text (good for dark-mode screenshots)
  //   'light' — light surface + dark text (good for light-mode screenshots)
  //   'auto'  — follow the active app theme via var(--surface) / var(--fg-2)
  // When omitted/null we default to 'dark', because most demo thumbnails are
  // captured in dark mode and a light overlay reads jarringly over them.
  preview_hover_gradient_theme?: DemoPreviewHoverGradientTheme | null;
}

export interface InteractionSummary {
  id: string;
  prompt: string;
  started_at: string;
  completed_at: string | null;
  error: string | null;
  turn_count: number;
  file_paths?: string[];
  source?: RunSource;
}

export interface PagedInteractions {
  items: InteractionSummary[];
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface InteractionDetail {
  id: string;
  prompt: string;
  result: string | null;
  error: string | null;
  started_at: string;
  completed_at: string | null;
  turns: Array<Record<string, unknown>>;
  file_paths?: string[];
  source?: RunSource;
}

export interface ChatResponse {
  run_id: string;
  session_id: string;
}

/** Alternative POST /api/chat response, returned by `$$ <cmd>` dev
 *  commands that should open a URL in a new tab instead of starting a
 *  chat run. Distinguished from `ChatResponse` by the presence of `url`. */
export interface NewTabResponse {
  url: string;
  target?: string;
}

/** Alternative POST /api/chat response that asks the client to show a
 *  sonner toast and otherwise do nothing. Distinguished by `text`. */
export interface ToastResponse {
  text: string;
  level?: 'info' | 'success' | 'warning' | 'error';
  description?: string | null;
  duration_ms?: number | null;
}

/** Alternative POST /api/chat response that renders an inline console
 *  block under the chat input (replacing any prior block). Distinguished
 *  by `sections`. */
export interface ConsoleResponse {
  sections: string[];
  title?: string | null;
}

export type ChatPostResponse =
  | ChatResponse
  | NewTabResponse
  | ToastResponse
  | ConsoleResponse;

export function isNewTabResponse(r: ChatPostResponse): r is NewTabResponse {
  return typeof (r as NewTabResponse).url === 'string';
}

export function isToastResponse(r: ChatPostResponse): r is ToastResponse {
  return typeof (r as ToastResponse).text === 'string';
}

export function isConsoleResponse(r: ChatPostResponse): r is ConsoleResponse {
  return Array.isArray((r as ConsoleResponse).sections);
}
